import React from 'react';
import { useTheme } from '@mui/material';
import { Box } from '../Box';
import { Text } from '../Text';
import type { BoxProps } from '../Box';

/** Variants map to design system indicator colors (Brand = primary, Neutral, Alert, Dark). */
export type IndicatorVariant = 'primary' | 'neutral' | 'alert' | 'dark';

export type IndicatorSize = 'small' | 'large';

const SIZE_VARIANTS: Record<
  IndicatorSize,
  { variant: 'label/sm' | 'label/md'; uppercase: boolean }
> = {
  small: { variant: 'label/sm', uppercase: false },
  large: { variant: 'label/md', uppercase: true },
};

/** Spacing factors for theme.spacing() — small: 6px h / 3px v, large: 8px h / 4px v; cornerRadius: 6px. */
const PADDING_H: Record<IndicatorSize, number> = { small: 1.5, large: 2 };
const PADDING_V: Record<IndicatorSize, number> = { small: 0.75, large: 1 };
const CORNER_RADIUS_FACTOR = 1.5;

export interface IndicatorProps extends Omit<BoxProps, 'children'> {
  /** Color variant. Primary (Brand), neutral, alert, or dark. */
  variant?: IndicatorVariant;
  /** Size: small or large (default). Height is driven by padding and typography. */
  size?: IndicatorSize;
  /** Content to show (e.g. count). */
  children: React.ReactNode;
}

/**
 * Count Indicator — small inline component for quantities or activity counts.
 * Uses theme: componentTokens.indicators.colors and typographyTokens for fontWeight.
 */
export const Indicator = React.forwardRef<HTMLDivElement, IndicatorProps>(function Indicator(
  { variant = 'primary', size = 'large', children, sx, ...rest },
  ref,
) {
  const theme = useTheme();
  const { componentTokens, typographyTokens } = theme.palette ?? {};
  const variantStyle = SIZE_VARIANTS[size];

  const horizontalPaddingFactor = PADDING_H[size];
  const verticalPaddingFactor = PADDING_V[size];

  const indicators = componentTokens?.indicators;
  const backgroundColor = indicators?.colors[variant]?.background?.resting;
  const color = indicators?.colors[variant]?.content?.text?.resting;
  const fontWeight = typographyTokens?.fontWeights?.semibold ?? 600;

  return (
    <Box
      ref={ref}
      display="inline-flex"
      alignContent="center"
      alignItems="center"
      justifyContent="center"
      backgroundColor={backgroundColor}
      sx={{
        borderRadius: theme.spacing(CORNER_RADIUS_FACTOR),
        paddingLeft: theme.spacing(horizontalPaddingFactor),
        paddingRight: theme.spacing(horizontalPaddingFactor),
        paddingTop: theme.spacing(verticalPaddingFactor),
        paddingBottom: theme.spacing(verticalPaddingFactor),
        textAlign: 'center',
        ...(variantStyle.uppercase && { textTransform: 'uppercase' as const }),
        ...sx,
      }}
      {...rest}
    >
      <Text
        variant={variantStyle.variant}
        fontWeight={fontWeight}
        color={color}
        sx={{ lineHeight: 1, whiteSpace: 'nowrap' }}
      >
        {children}
      </Text>
    </Box>
  );
});
